import React from 'react';
import { useTodoContext } from '../context/TodoContext';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

const SharedTodosList = () => {
  const { sharedTodos, loading, error, updateTodo, deleteTodo } = useTodoContext();

  if (loading) {
    return (
      <div className="flex justify-center items-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-500 dark:text-red-400 p-4" role="alert">
        Errore: {error}
      </div>
    );
  }

  if (!sharedTodos || sharedTodos.length === 0) {
    return (
      <div className="text-gray-500 dark:text-gray-400 text-center p-4">
        Nessun task condiviso con te
      </div>
    );
  }

  const handleToggleComplete = async (todo) => {
    try {
      await updateTodo(todo._id, {
        ...todo,
        completed: !todo.completed
      });
    } catch (err) {
      console.error('Errore durante l\'aggiornamento del task:', err);
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'text-red-500 dark:text-red-400';
      case 'medium':
        return 'text-yellow-500 dark:text-yellow-400';
      case 'low':
        return 'text-green-500 dark:text-green-400';
      default:
        return 'text-gray-500 dark:text-gray-400';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'high':
        return 'Alta';
      case 'medium':
        return 'Media';
      case 'low':
        return 'Bassa';
      default:
        return 'Non specificata';
    }
  };

  return (
    <div className="space-y-4">
      {sharedTodos.map((todo) => (
        <div
          key={todo._id}
          className={`
            flex items-center justify-between p-4 rounded-lg
            ${todo.completed 
              ? 'bg-gray-100 dark:bg-gray-700/50' 
              : 'bg-white dark:bg-gray-700'
            }
            shadow-sm hover:shadow-md transition-shadow
          `}
        >
          <div className="flex items-center space-x-4 flex-1">
            <input
              type="checkbox"
              checked={todo.completed}
              onChange={() => handleToggleComplete(todo)}
              className="w-5 h-5 rounded border-gray-300 text-blue-500 focus:ring-blue-500 cursor-pointer"
            />
            
            <div className="flex-1">
              <div className={`text-sm font-medium ${todo.completed ? 'line-through text-gray-500' : 'text-gray-900 dark:text-gray-100'}`}>
                {todo.text}
              </div>
              
              <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 space-x-2">
                {todo.user && (
                  <span>Condiviso da: {todo.user.name}</span>
                )}
                
                <span className={getPriorityColor(todo.priority)}>
                  • Priorità: {getPriorityLabel(todo.priority)}
                </span>
                
                {todo.dueDate && (
                  <span>
                    • Scadenza: {format(new Date(todo.dueDate), 'dd MMM yyyy HH:mm', { locale: it })}
                  </span>
                )}
              </div>
              
              {todo.categories && todo.categories.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-2">
                  {todo.categories.map(category => (
                    <span
                      key={category._id}
                      className="px-2 py-1 text-xs rounded-full"
                      style={{
                        backgroundColor: category.color,
                        color: getContrastYIQ(category.color)
                      }}
                    >
                      {category.name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {todo.sharedWith?.find(share => share.user === todo.user._id)?.permissions.includes('delete') && (
              <button
                onClick={() => deleteTodo(todo._id)}
                className="p-2 text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors"
                title="Elimina task"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

// Funzione di supporto per determinare il colore del testo in base al colore di sfondo
const getContrastYIQ = (hexcolor) => {
  if (!hexcolor || hexcolor === 'undefined') return '#ffffff';
  hexcolor = hexcolor.replace('#', '');
  const r = parseInt(hexcolor.substr(0, 2), 16);
  const g = parseInt(hexcolor.substr(2, 2), 16);
  const b = parseInt(hexcolor.substr(4, 2), 16);
  const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  return (yiq >= 128) ? '#000000' : '#ffffff';
};

export default SharedTodosList;